import {
	TypographyCaps,
	TypographyLabel,
	TypographyParagraph,
} from '../typography'
import clsx from 'clsx'
import { useDropzone } from 'react-dropzone-esm'
import { useEffect, useState } from 'react'
import UploadImageIcon from '../icons/Actions/UploadImageIcon'
import UploadArrow from '../icons/Arrows/UploadArrow'

const ArtistAvatarImageInputField = ({
	name,
	id,
	helper,
	uploadDescription,
	aspectRatio = 1,
	isValid,
	isError,
	isInactive = false,
	icon,
	additionalClasses = '',
	containerWidth = 'w-full',
	displayRequired,
	displayName = true,
	lines = 1,
	value = undefined,
	onChange = () => {},
}: {
	name: string
	id?: string
	helper?: string
	uploadDescription?: string
	aspectRatio?: number
	placeholder?: string
	isValid?: boolean
	isError?: boolean
	isInactive?: boolean
	icon?: React.ReactNode
	additionalClasses?: string
	containerWidth?: string
	displayRequired?: boolean
	displayName?: boolean
	lines?: number
	value?: string
	onChange?: (e: any) => void
}) => {
	const [imageToRender, setImageToRender] = useState(
		value && value !== undefined && value !== '' ? value : undefined,
	)

	useEffect(() => {
		if (imageToRender === undefined && value !== undefined && value !== '') {
			setImageToRender(value)
		}
	}, [value, imageToRender])

	const { getRootProps, getInputProps, open } = useDropzone({
		accept: {
			'image/jpeg': [],
			'image/png': [],
			'image/gif': [],
			'image/svg': [],
		},
		noClick: true,
		onDrop: acceptedFiles => {
			if (acceptedFiles.length) {
				const fileToRender = acceptedFiles[acceptedFiles.length - 1]
				setImageToRender(getURLForFile(fileToRender))
				onChange(fileToRender)
			}
		},
	})

	const getURLForFile = (file: File) => {
		return URL.createObjectURL(file)
	}

	return (
		<div className={containerWidth}>
			{displayName && (
				<label
					htmlFor={name}
					className="block leading-6 text-grey400 dark:text-grey500"
				>
					<TypographyCaps size="small">{`${name}${displayRequired ? ' *' : ''}`}</TypographyCaps>
				</label>
			)}
			{helper && (
				<TypographyParagraph
					size="medium"
					additionalClasses={clsx(
						'mt-1',
						isError
							? 'text-red'
							: 'text-textPrimaryLight dark:text-textPrimaryDark',
					)}
				>
					{helper}
				</TypographyParagraph>
			)}
			<div
				{...getRootProps({
					className: clsx(
						'dropzone group relative mt-2 w-full items-center justify-center cursor-pointer flex border rounded-lg hover:transition-all ease-in-out hover:duration-300',
						isError
							? 'border-red'
							: 'border-textInactiveLight dark:border-textInactiveDark hover:border-textPrimaryLight hover:dark:border-textPrimaryDark',
					),
				})}
			>
				<div className="w-full">
					<input {...getInputProps()} />
					<div
						className="relative flex min-h-[88px] w-full cursor-pointer flex-col items-center justify-center p-2"
						onClick={() => open()}
					>
						<div
							// style={{ width: `${aspectRatio * 104}px` }}
							className={clsx(
								`absolute flex w-full flex-col items-center justify-center object-contain text-textPrimaryLight ease-in-out group-hover:transition-all group-hover:duration-300 dark:text-textPrimaryDark`,
								imageToRender
									? 'my-4 h-[104px] opacity-0 group-hover:opacity-100'
									: 'opacity-100',
							)}
						>
							<div
								className={`w-[${aspectRatio * 104}px] text-textSecondaryLight group-hover:text-textPrimaryLight dark:text-textSecondaryDark group-hover:dark:text-textPrimaryDark`}
							>
								<UploadImageIcon />
							</div>
							<TypographyLabel
								size={'medium'}
								isBold={true}
								additionalClasses="text-center w-[200px] mt-2"
							>
								Upload Avatar
							</TypographyLabel>
						</div>

						{imageToRender && (
							<img
								src={imageToRender}
								style={{ width: `${aspectRatio * 104}px` }}
								className={clsx(
									'transition-all duration-300 ease-in-out',
									imageToRender
										? 'opacity-100 group-hover:opacity-50'
										: 'opacity-0',
									`my-4 h-[104px] w-[${aspectRatio * 104}px] rounded-lg border border-textInactiveLight object-cover group-hover:border-textPrimaryLight dark:border-textInactiveDark group-hover:dark:border-textPrimaryDark`,
								)}
								alt={`${name}-image-input`}
							/>
						)}
					</div>
				</div>
			</div>
			<TypographyParagraph
				size="xSmall"
				additionalClasses={
					'text-textSecondaryLight dark:text-textSecondaryDark mt-1'
				}
			>
				{uploadDescription || ''}
			</TypographyParagraph>
		</div>
	)
}

export default ArtistAvatarImageInputField
